import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  Space,
  Tooltip,
  Button,
  PageHeader,
  Tag,
  Result,
  Spin,
  Input,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  handleApiSuccess,
  handleApiError,
  confirmDelete,
  accessControls,
} from 'utils';
import {
  timeTrackerProjectsListService,
  timeTrackerProjectDeleteService,
} from 'services/time_tracker';
import { getProjectColor } from 'utils/time_tracker_project_color';

const { Search } = Input;

export default function TimeTrackerProjectsList() {
  const history = useHistory();
  const [permissions, setPermissions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    accessControls
      .fetch()
      .then(setPermissions)
      .catch(() => setPermissions({}));
  }, []);

  const columns = [
    {
      title: 'Company / Branch',
      key: 'company_branch',
      render: (_, record) => {
        const companyName = record.company?.company_name || '';
        const branchName = record.branch?.branch_name || '';
        const parts = [companyName, branchName].filter(Boolean);
        return parts.length ? parts.join(' / ') : '-';
      },
    },
    {
      title: 'Project Name',
      dataIndex: 'project_name',
      key: 'project_name',
      sorter: (a, b) =>
        (a.project_name || '').localeCompare(b.project_name || ''),
      render: (text, record) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              flexShrink: 0,
              background: getProjectColor(record),
            }}
          />
          {record.project_name}
        </span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'project_description',
      key: 'project_description',
      ellipsis: true,
      render: (t) => t || '-',
    },
    {
      title: 'Access groups',
      key: 'assigned_groups',
      render: (_, record) => {
        const groups = record.assigned_groups;
        if (!groups || groups.length === 0) {
          const hint =
            'No access groups yet—most users will not see this project until at least one group is assigned here.';
          return (
            <Tooltip title={hint}>
              <Tag color="warning" style={{ margin: 0, cursor: 'default' }}>
                None
              </Tag>
            </Tooltip>
          );
        }
        return groups.map((g) => (
          <Tag key={g._id || g}>{g.group_name || g._id}</Tag>
        ));
      },
    },
    {
      title: (
        <Tooltip
          title="Users here can access the project once access groups exist. Super Admin (and related privileged roles) see all projects and can be included on any team."
        >
          <span style={{ cursor: 'help', borderBottom: '1px dotted rgba(0, 0, 0, 0.25)' }}>
            Team
          </span>
        </Tooltip>
      ),
      dataIndex: 'team',
      key: 'team',
      render: (team) =>
        team && team.length
          ? team.map((u) => (
              <Tag key={u._id}>{u.display_name || u.first_name || u.email}</Tag>
            ))
          : '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          color={
            status === 'active'
              ? 'green'
              : status === 'archived'
              ? 'default'
              : 'orange'
          }
        >
          {status || 'active'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View detail & analytics">
            <Button
              shape="circle"
              size="small"
              icon={<EyeOutlined />}
              onClick={() =>
                history.push('/time-tracker/projects/view/' + record._id)
              }
            />
          </Tooltip>
          {accessControls.can(permissions, 'TimeTrackerProject.update:any') && (
            <Tooltip title="Edit">
              <Button
                type="primary"
                shape="circle"
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                  history.push('/time-tracker/projects/update/' + record._id)
                }
              />
            </Tooltip>
          )}
          {accessControls.can(permissions, 'TimeTrackerProject.delete:any') && (
            <Tooltip title="Delete">
              <Button
                shape="circle"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteRecord(record._id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const loadData = () => {
    setIsLoading(true);
    timeTrackerProjectsListService()
      .then((res) => {
        setIsLoading(false);
        setDataSource(res.data.items || []);
      })
      .catch((err) => {
        setIsLoading(false);
        handleApiError(err);
      });
  };

  useEffect(() => {
    if (permissions == null) return;
    if (!accessControls.can(permissions, 'TimeTracker.read:any')) return;
    loadData();
  }, [permissions]);

  const filteredDataSource = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return dataSource;
    return dataSource.filter((record) => {
      const projectName = record.project_name || '';
      const description = record.project_description || '';
      const companyName = record.company?.company_name || '';
      const branchName = record.branch?.branch_name || '';
      const status = record.status || '';
      const groupNames = (record.assigned_groups || [])
        .map((g) => g.group_name || '')
        .join(' ');
      const teamNames = (record.team || [])
        .map((u) => u.display_name || u.first_name || u.email || '')
        .join(' ');
      const haystack = [
        projectName,
        description,
        companyName,
        branchName,
        status,
        groupNames,
        teamNames,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [dataSource, searchQuery]);

  const deleteRecord = (id) => {
    confirmDelete().then(() => {
      setIsLoading(true);
      timeTrackerProjectDeleteService(id)
        .then((res) => {
          setIsLoading(false);
          handleApiSuccess(res);
          loadData();
        })
        .catch((err) => {
          setIsLoading(false);
          handleApiError(err);
        });
    });
  };

  if (permissions == null) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!accessControls.can(permissions, 'TimeTracker.read:any')) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="403"
          title="Access denied"
          subTitle="You do not have permission to view Time Tracker Projects."
          extra={
            <Button type="primary" onClick={() => history.push('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  const canCreate = accessControls.can(permissions, 'TimeTrackerProject.create:any');

  return (
    <div>
      <PageHeader
        ghost={false}
        title="Time Tracker Projects"
        subTitle="Create and manage projects linked to company and branch"
        extra={[
          <Search
            key="search"
            allowClear
            placeholder="Search projects, company, branch, team..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onSearch={(value) => setSearchQuery(value)}
            style={{ width: 280 }}
          />,
          ...(canCreate
            ? [
                <Button
                  key="create"
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={() => history.push('/time-tracker/projects/create')}
                >
                  New Project
                </Button>,
              ]
            : []),
        ]}
      />
      <Table
        rowKey="_id"
        loading={isLoading}
        dataSource={filteredDataSource}
        columns={columns}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} projects`,
        }}
        size="small"
      />
    </div>
  );
}
